/* ==========================================================================
   Free Gift Wrap (Custom) — standalone cart card controller
   --------------------------------------------------------------------------
   - Renders the gift-wrap card (all states from gift-wrap-mockup.html) into
     #gift-wrap-card, a static mount point inside the theme's Vue cart drawer.
   - Mutates the cart via the Shopify AJAX API, then asks the theme to refresh
     (s-ajaxcart.fetch) so Domaine's cart UI + this card update together.
   - Re-renders on the theme's document cart events (cart:*:complete).
   - The FREE label is DISPLAY ONLY. The real $0 is applied by the Shopify
     Function at cart + checkout. See GIFT_SPEC.md.
   ========================================================================== */
(function () {
  'use strict';

  var dataEl = document.getElementById('gift-wrap-card-data');
  if (!dataEl) return;

  var cfg;
  try { cfg = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!cfg || !cfg.variantId || !cfg.thresholdCents) return;

  var MOUNT_ID = 'gift-wrap-card';

  // Current state captured at last render, used by action handlers.
  var current = { giftKey: null, qty: 0 };
  var busy = false;
  var rafPending = false;

  // --- SVG icons (mirror the mockup) --------------------------------------
  var GIFT_SVG = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#9b9b9b" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8S13 3 16.5 3a2.5 2.5 0 0 1 0 5"/></svg>';
  var TRASH_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>';
  var CHECK_SVG = '<svg class="gwc__check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6"/></svg>';

  // --- helpers ------------------------------------------------------------
  function money(cents) {
    if (window.theme && theme.utils && typeof theme.utils.formatMoney === 'function') {
      try { return theme.utils.formatMoney(cents); } catch (e) {}
    }
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function findGiftLine(cart) {
    return (cart.items || []).find(function (i) {
      return i.properties && (i.properties._gift_wrap === 'true' || i.properties._gift_wrap === true);
    });
  }

  function eligibleCents(cart, giftKey) {
    return (cart.items || []).reduce(function (sum, i) {
      if (giftKey && i.key === giftKey) return sum;
      if (i.vendor === 'Order Protection') return sum; // match theme's free-ship convention
      return sum + i.final_line_price;
    }, 0);
  }

  // --- theme sync ---------------------------------------------------------
  function refreshTheme() {
    var el = document.querySelector('s-ajaxcart');
    if (el && typeof el.fetch === 'function') {
      el.fetch(false); // refetch cart without opening the drawer; fires cart:fetch:complete
    } else {
      scheduleRender();
    }
  }

  // --- cart mutations -----------------------------------------------------
  function addGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: cfg.variantId, quantity: 1, properties: { _gift_wrap: 'true' } })
    });
  }

  function changeGift(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  function handleAction(action) {
    if (busy) return;
    var qty = current.qty;
    var key = current.giftKey;
    var op;

    if (action === 'add') {
      op = addGift();
    } else if (action === 'plus') {
      if (qty >= cfg.maxQty) return;
      op = key ? changeGift(key, qty + 1) : addGift();
    } else if (action === 'minus') {
      if (!key || qty <= 0) return;
      op = changeGift(key, qty - 1);
    } else if (action === 'remove') {
      if (!key) return;
      op = changeGift(key, 0);
    } else {
      return;
    }

    busy = true;
    setLoading(true);
    op.catch(function () {}).then(function () {
      busy = false;
      refreshTheme();
      scheduleRender();
    });
  }

  function setLoading(on) {
    var node = document.getElementById(MOUNT_ID);
    if (!node) return;
    var card = node.querySelector('.gwc__card');
    if (card) card.classList.toggle('is-loading', !!on);
  }

  // --- rendering ----------------------------------------------------------
  function scheduleRender() {
    if (rafPending) return;
    rafPending = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      rafPending = false;
      render();
    }, 0);
  }

  function render() {
    var node = document.getElementById(MOUNT_ID);
    if (!node) return; // mount not present (empty cart / Vue not mounted yet)

    getCart().then(function (cart) {
      node = document.getElementById(MOUNT_ID);
      if (!node) return;

      var gift = findGiftLine(cart);
      var qty = gift ? gift.quantity : 0;
      var eligible = eligibleCents(cart, gift && gift.key);

      current.giftKey = gift ? gift.key : null;
      current.qty = qty;

      var availableFree = Math.floor(eligible / cfg.thresholdCents);
      var freeUnits = Math.min(availableFree, qty);
      var paidUnits = qty - freeUnits;
      var paidTotal = paidUnits * cfg.unitPrice;
      var nextAddFree = availableFree > qty;
      var rem = eligible % cfg.thresholdCents;
      var away = cfg.thresholdCents - ((rem === 0 && eligible > 0) ? cfg.thresholdCents : rem);
      var claim = qty === 0 && availableFree > 0;
      var soldOut = cfg.available === false;
      var atMax = qty >= cfg.maxQty;

      node.innerHTML = buildCard({
        qty: qty, availableFree: availableFree, freeUnits: freeUnits,
        paidUnits: paidUnits, paidTotal: paidTotal, nextAddFree: nextAddFree,
        away: away, claim: claim, soldOut: soldOut, atMax: atMax
      });
    }).catch(function () {});
  }

  function thumb(claim) {
    var inner = cfg.image
      ? '<img src="' + esc(cfg.image) + '" alt="' + esc(cfg.productTitle) + '" width="72" height="72">'
      : GIFT_SVG;
    return '<div class="gwc__thumb">' + inner + '</div>';
  }

  function buildCard(s) {
    // Out of stock, nothing in cart yet -> unavailable card.
    if (s.soldOut && s.qty === 0) {
      return '' +
        '<div class="gwc__card is-unavailable"><div class="gwc__row">' + thumb(false) +
          '<div class="gwc__body">' +
            '<div class="gwc__head"><div class="gwc__title">' + esc(cfg.title) + '</div>' +
              '<span class="gwc__remove-spacer"></span></div>' +
            '<div class="gwc__na">Currently unavailable</div>' +
          '</div></div></div>';
    }

    // --- middle (subtitle / badges) ---
    var mid, price;
    if (s.qty === 0 && s.availableFree > 0) {
      mid = '<div class="gwc__subtitle is-green">' + CHECK_SVG + 'Your free gift is ready</div>';
      price = '<span class="gwc__price is-free">FREE</span>' +
              '<span class="gwc__price-strike">' + money(cfg.unitPrice) + '</span>';
    } else if (s.qty === 0) {
      mid = '<div class="gwc__subtitle is-muted">' + money(s.away) + ' away from a free gift</div>';
      price = '<span class="gwc__price">' + money(cfg.unitPrice) + '</span>';
    } else {
      var badges = '';
      if (s.freeUnits > 0) badges += '<span class="gwc__badge gwc__badge--free">FREE ×' + s.freeUnits + '</span>';
      if (s.paidUnits > 0) badges += '<span class="gwc__badge gwc__badge--paid">' + money(cfg.unitPrice) + ' ×' + s.paidUnits + '</span>';
      mid = '<div class="gwc__badges">' + badges + '</div>';
      price = s.paidTotal === 0
        ? '<span class="gwc__price is-free">' + money(0) + '</span>'
        : '<span class="gwc__price">' + money(s.paidTotal) + '</span>';
    }

    // --- stepper / add ---
    var stepper;
    if (s.qty === 0) {
      var addClass = s.availableFree > 0 ? 'gwc__circ--green' : 'gwc__circ--dark';
      var addDisabled = s.soldOut ? ' disabled' : '';
      stepper = '<button type="button" data-gwc-action="add" class="gwc__circ ' + addClass + '"' + addDisabled + ' aria-label="Add gift">+</button>';
    } else {
      var plusClass = s.nextAddFree ? 'gwc__circ--green' : 'gwc__circ--dark';
      var plusDisabled = (s.atMax || s.soldOut) ? ' disabled' : '';
      stepper = '<div class="gwc__stepper">' +
        '<button type="button" data-gwc-action="minus" class="gwc__circ gwc__circ--outline" aria-label="Decrease">−</button>' +
        '<span class="gwc__qty">' + s.qty + '</span>' +
        '<button type="button" data-gwc-action="plus" class="gwc__circ ' + plusClass + '"' + plusDisabled + ' aria-label="Increase">+</button>' +
      '</div>';
    }

    // --- trash ---
    var trash = s.qty > 0
      ? '<button type="button" data-gwc-action="remove" class="gwc__remove" aria-label="Remove gift">' + TRASH_SVG + '</button>'
      : '<span class="gwc__remove-spacer"></span>';

    return '' +
      '<div class="gwc__card' + (s.claim ? ' is-claim' : '') + '"><div class="gwc__row">' + thumb(s.claim) +
        '<div class="gwc__body">' +
          '<div class="gwc__head"><div class="gwc__title">' + esc(cfg.title) + '</div>' + trash + '</div>' +
          mid +
          '<div class="gwc__foot"><div>' + price + '</div>' + stepper + '</div>' +
        '</div></div></div>';
  }

  // --- wiring -------------------------------------------------------------
  // Delegated click handler on document survives Vue re-mounting the node.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-gwc-action]');
    if (!btn || !btn.closest('#' + MOUNT_ID)) return;
    e.preventDefault();
    handleAction(btn.getAttribute('data-gwc-action'));
  });

  document.addEventListener('cart:fetch:complete', scheduleRender);
  document.addEventListener('cart:update:complete', scheduleRender);
  document.addEventListener('cart:add:complete', scheduleRender);

  // Initial render + a few retries in case the Vue cart mounts after this script.
  function boot() {
    render();
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (document.getElementById(MOUNT_ID) || tries > 20) {
        render();
        clearInterval(iv);
      }
    }, 250);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
